import { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { observer } from 'mobx-react';
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AxiosError } from 'axios';
import {
  useCreateVivaIntegration,
  useIntegrationsList,
  useCreatePersonalkollenIntegration,
} from '../../../stores/FetcherStores/integrationFetcher';
import useCompanyId from '../../../hooks/useCompanyId';
import { useCompany } from '../../../stores';
import { VivaISV, VivaISVTurnover } from '../../../constants/isv';

interface AddIntegrationModalProps {
  open: boolean;
  onClose: () => void;
}

const possibleIntegrations = ['VIVA', 'PERSONALKOLLEN'];

const AddIntegrationModal: FC<AddIntegrationModalProps> = observer(({ open, onClose }) => {
  const companyId = useCompanyId();
  const { data: company } = useCompany();
  const { data: integrations } = useIntegrationsList(companyId);
  const { mutate: createVivaIntegration, isLoading: isVivaLoading } = useCreateVivaIntegration();
  const { mutateAsync: createPersonalkollenIntegration, isLoading: isPersonalkollenLoading } =
    useCreatePersonalkollenIntegration();
  const [personalkollenApiKey, setPersonalkollenApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  const availableIntegrations = useMemo(() => {
    // Filter out integrations that are already added
    const unusedIntegrations = possibleIntegrations.filter((integration) => {
      return !integrations?.some((i) => i.type === integration);
    });

    // Filter based on company subscriptions
    return unusedIntegrations.filter((integration) => {
      if (integration === 'PERSONALKOLLEN') {
        const hasPersonalkollenSubscription = company?.subscriptions?.some(
          (sub) => sub.id === 'PERSONALKOLLEN' && sub.data.status === 'active',
        );
        return hasPersonalkollenSubscription;
      }
      return true;
    });
  }, [integrations, company?.subscriptions]);

  const getIntegrationDetails = (integration: string) => {
    switch (integration) {
      case 'VIVA':
        return {
          title: 'Viva wallet kortbetalning',
          description:
            'Starta onboarding med Viva här. När den är tillagd och aktiv kan du lägga in viva som betalmetod, koppla viva terminaler eller köra igång med Enklaorder. Avgift från 0.69%',
        };
      case 'PERSONALKOLLEN':
        return {
          title: 'Personalkollen',
          description:
            'Integrera med Personalkollen för att hantera personal, schemaläggning och löner.',
        };
      default:
        return {
          title: integration,
          description: '',
        };
    }
  };

  const handleCreateIntegration = async (integration: string) => {
    setError(null);
    try {
      if (integration === 'VIVA') {
        await createVivaIntegration(companyId, {
          turnover: VivaISVTurnover.DEFAULT,
          terminalFee: VivaISV.FORTYTWO,
        });
        onClose();
      } else if (integration === 'PERSONALKOLLEN') {
        if (!personalkollenApiKey.trim()) {
          setError('API nyckeln måste vara ifylld');
          return;
        }
        await createPersonalkollenIntegration({ companyId, apiKey: personalkollenApiKey })
          .then(() => {
            onClose();
          })
          .catch((e) => {
            if (e instanceof AxiosError) {
              if (e?.response?.data) {
                const errorData = e?.response?.data as { name: string };
                setError(errorData.name);
                return;
              }
            }
            throw e;
          });
      }
    } catch (e) {
      captureException(e);
      setError('Något gick fel, försök igen senare, eller kontakta support.');
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 600,
          height: '100%',
          paddingRight: 4,
          paddingLeft: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            marginTop: 12,
            marginBottom: 2,
            display: 'flex',
            minHeight: 'initial !important',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              marginBottom: 2,
            }}
          >
            <Typography variant="h4">Lägg till integration</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          {availableIntegrations.length > 0 ? (
            availableIntegrations.map((integration) => {
              const details = getIntegrationDetails(integration);
              const isLoading = integration === 'VIVA' ? isVivaLoading : isPersonalkollenLoading;

              return (
                <Box key={integration} sx={{ marginTop: 2 }}>
                  <Typography variant="h6">{details.title}</Typography>
                  <Typography>{details.description}</Typography>

                  {integration === 'PERSONALKOLLEN' && (
                    <>
                      {error && (
                        <Box mt={2}>
                          <Alert severity="error">{error}</Alert>
                        </Box>
                      )}
                      <TextField
                        fullWidth
                        label="API Nyckel"
                        value={personalkollenApiKey}
                        onChange={(e) => {
                          setPersonalkollenApiKey(e.target.value);
                          setError(null);
                        }}
                        type="password"
                        variant="outlined"
                        sx={{ mt: 2 }}
                        required
                      />
                    </>
                  )}

                  <Button
                    disabled={
                      isLoading ||
                      (integration === 'PERSONALKOLLEN' && !personalkollenApiKey.trim())
                    }
                    sx={{ marginTop: 2 }}
                    variant="contained"
                    onClick={() => handleCreateIntegration(integration)}
                  >
                    Lägg till
                  </Button>
                </Box>
              );
            })
          ) : (
            <Box sx={{ marginTop: 2 }}>
              <Alert severity="info">Inga tillgängliga integrationer</Alert>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
});

export default AddIntegrationModal;
